/* Library workspace — the shelf, and the search over it.

   Two modes on one surface, as in v1: browse a paged grid, or — the moment the
   header query is non-empty — a ranked list of matched transcript sentences.
   Structure matches Index (§8, browsing workspaces share one mental model):
   sticky bar → content.

   Decisions worth knowing:

   - Category chips stay up in BOTH modes, and during a search their counts come
     from /api/search/facets, so they answer "where are my matches" rather than
     "how big is my library". The All chip therefore carries the result count,
     which is why there is no separate count line.
   - Both modes page from the same IntersectionObserver sentinel, 60 at a time
     browsing and 40 searching (the server's own caps).
   - Picking a category with zero matches is NOT corrected back to All. Index
     does derive that fallback, but here the other chips show exactly where the
     matches went, so silently changing the user's filter would hide the one
     piece of information they need to act on.
   - No list virtualisation, deliberately (ADR 0002 §8) — ~1,700 tiles of mostly
     lazy images is not where this app's performance goes.
   - Clicking anything opens Detail on the whole visible list, not on the one
     item — see `openItem`. */

import { LitElement, html, css } from 'lit';
import { customElement, property, state, query as domQuery } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import type { Facets, Intent, Item, SearchItem, WorkspaceDef } from '../../core/types.js';
import type { ChipOption } from '../../component/g-chips/g-chips.js';
import type { GFilterBar } from '../../component/g-filter-bar/g-filter-bar.js';
import {
  PAGE_SIZE,
  SEARCH_PAGE_SIZE,
  getItems,
  getMeta,
  getSearchFacets,
  isSignedIn,
  searchItems,
} from '../../core/api.js';
import { clean } from '../../../../packages/core/index.js';
import { watchBandCollapse, type ScrollDirectionWatcher } from '../../core/scroll-direction.js';
import '../../component/g-filter-bar/g-filter-bar.js';
import '../../component/g-notice/g-notice.js';
import './item-tile.js';
import './item-row.js';

export const libraryDef: WorkspaceDef = {
  id: 'library',
  label: 'Library',
  icon: 'library',
  tag: 'g-library',
  zone: 'top',
  searchable: true,
  searchPlaceholder: 'Search what they said…',
  refreshable: true,
};

@customElement('g-library')
export class GLibrary extends LitElement {
  static override styles = [
    shadowReset,
    css`
      :host {
        display: block;
      }
      /* Fluid, not v1's hardcoded 3 columns — that was a phone figure, and three
         420px tiles on a desktop is a slideshow. Six columns at 1280px, and it
         degrades to 2 on a narrow window without a media query.

         The gap is a real gap (v1 used 3px, near-tiling): at desktop density the
         tiles need separating, and the grid is the one place the warm background
         still shows through. */
      .grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
        gap: var(--space-2);
        padding: var(--space-4) var(--gutter-outer) var(--space-6);
      }
      .results {
        padding: var(--space-3) var(--content-r) var(--space-6) var(--content-l);
      }
      /* The phone grid (ADR 0009 decision 3, Library pass): three columns, edge
         to edge, near-tiling.

         v1's hardcoded 3 columns was called "a phone figure" in the note above
         and replaced by the fluid track — correctly, for desktop. On a 360px
         phone the fluid rule resolves to 2 columns of 152px, which reads as a
         list of cards rather than a library you scan. Three is the reel-grid
         idiom and it is what the phone was right about.

         2px rather than a space token: at this density the gap is a seam, not a
         gutter, and --space-1 (4px) already reads as a border. There is no 2px
         token because this is the only place that wants one — the grid is
         deliberately the exception to the handover, being tiles rather than
         rows.

         The treatment (bleed, seam, square) is keyed on width; only the column
         COUNT is keyed on orientation, so rotating the phone changes the density
         and nothing else. Three fixed columns is a portrait figure: at 800x360
         it would give 265px tiles, 353px tall in a viewport with ~264px of room,
         so not one tile would fit on screen. Landscape falls to a fluid track
         instead.

         Fluid can't serve portrait either, for the opposite reason: the minmax
         that yields 3 columns at 360px yields only 2 at 320px, and small phones
         are exactly where 3 matters most. Hence a count per orientation rather
         than one clever track. */
      @media (max-width: 959px) {
        .grid {
          grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
          gap: 2px;
        }
        /* §21 wants the skeleton shaped like what replaces it, so it loses its
           corners with the tiles. */
        .skel {
          border-radius: 0;
        }
      }
      @media (max-width: 959px) and (orientation: portrait) {
        .grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }
      /* Inside the results block, not the bar: the ordering is a property of the
         list, and putting it in the bar would change the bar's height between
         modes. The count itself lives on the All chip. */
      .rank {
        margin: 0 0 var(--space-2);
        padding-left: var(--space-3);
        color: var(--text-dim);
        font-family: var(--font-mono);
        font-size: 0.72rem;
      }
      /* §21 — skeletons over spinners, shaped like what replaces them. */
      .skel {
        aspect-ratio: 3 / 4;
        border-radius: var(--radius-md);
        background: var(--surface);
        animation: pulse 1.4s ease-in-out infinite;
      }
      @keyframes pulse {
        50% {
          opacity: 0.45;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .skel {
          animation: none;
        }
      }
      /* Zero-height: it exists to be observed, not seen. The 600px rootMargin on
         the observer is what makes the next page arrive before it is reached. */
      .sentinel {
        height: 1px;
      }
      g-notice.page {
        padding: var(--space-6) var(--content-r) var(--space-6) var(--content-l);
      }
    `,
  ];

  @property() query = '';

  @state() private items: Item[] = [];
  @state() private results: SearchItem[] = [];
  /** Whole-library counts, for the chips while browsing. */
  @state() private libraryCats: ChipOption[] = [];
  /** Per-category match counts for `facetsFor`, for the chips while searching. */
  @state() private facets: Facets | null = null;
  @state() private category = '';
  /* Two flags, not one shared `loading`. The modes load independently — a search
     firing does not mean the grid behind it is stale — and one flag let the
     initial empty-query pass switch the grid to "loaded" while its first page was
     still in flight, so the grid rendered its empty state over 654 items.
     Both cover the COLD load only; a paged append must not blank what is on
     screen and replace it with skeletons. */
  @state() private gridLoading = true;
  @state() private searchLoading = false;
  @state() private error = '';

  @domQuery('.sentinel') private sentinel?: HTMLElement;
  @domQuery('g-filter-bar') private bar?: GFilterBar;
  private observer?: IntersectionObserver;
  private bandWatch?: ScrollDirectionWatcher;

  /** Per-mode paging cursors. `exhausted` after any short page, as in v1. */
  private gridOffset = 0;
  private gridExhausted = false;
  private searchOffset = 0;
  private searchExhausted = false;
  /* One mutex per mode, and it refuses only a load carrying the SAME token as
     the one already in flight. Both halves of that are load-bearing.

     Per-mode because the modes must not cancel each other's work — the same
     reason `pageToken` and `searchToken` are separate counters, and the single
     shared flag this replaced quietly contradicted that. That flag also refused
     restarts, which wedged the search skeleton on screen permanently, because a
     refused load still leaves the loading flag its caller had already set:

       query A in flight (paging = true) → query B arrives → restartSearch sets
       searchLoading = true, bumps the token, and its loadSearchPage returns on
       the mutex → A's response lands stale, so its `finally` skips clearing
       searchLoading → nothing is in flight, nothing will re-dispatch, and the
       sentinel has been continuously intersecting so it never fires again.

     Token-keyed rather than the `offset > 0` test that first fixed that wedge
     (ADR 0004 §6a, corrected by §6b). The intent was always to let a
     supersession past — the token already makes the older response harmless, so
     there is nothing left for a mutex to protect — and `offset === 0` was
     standing in for "this is a supersession". It is not the same thing. A COLD
     LOAD is offset 0 and supersedes nothing: connectedCallback's first fetch,
     the first-update `query` change, and the sentinel firing behind skeletons
     short enough to leave it in range are three genuine duplicates carrying one
     token, and the offset test waved all three through. Asking about the token
     asks the actual question.

     Between them, every path that sets a loading flag still owns a request that
     resolves it, which is what makes the stuck state unreachable rather than
     merely unlikely. */
  private gridPaging = false;
  private searchPaging = false;
  /* The token carried by each mode's in-flight request, so the mutex can tell a
     duplicate (same token) from a supersession (bumped past it). Only meaningful
     while the matching `*Paging` flag is set. */
  private gridInFlight = -1;
  private searchInFlight = -1;

  /* One token per mode; a response whose token is stale is dropped. Typing is
     debounced upstream but still overlaps on a slow request, and without this an
     earlier, broader result set can land last and win.

     Separate counters because the modes invalidate on different things — a new
     query only concerns the results — and a shared one made each mode cancel the
     other's in-flight work for no reason. A category change bumps both, since it
     applies to both. */
  private pageToken = 0;
  private searchToken = 0;

  /** The query the current `facets` describe; anything else makes them stale. */
  private facetsFor = '';

  private get searching(): boolean {
    return this.query.trim().length > 0;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    void this.loadLibraryCounts();
    void this.loadGridPage();
    // The host IS the scrollport — `#outlet > *` is where each workspace's own
    // scroll lives (ADR 0010) — so it is both the element to watch and the one
    // whose `hidden` says we came back to the front.
    this.bandWatch = watchBandCollapse(this, this, (c) => this.applyCollapse(c), {
      onReturn: () => this.bar?.closeSearch(),
    });
  }

  /**
   * Put the band out of the way while reading (ADR 0017 §4).
   *
   * Written straight to the child rather than held in @state: this flips mid-fling,
   * and a reactive write would re-run this workspace's render — rebuilding 654 tile
   * templates for Lit to diff and discard — at the one moment there is no frame
   * budget for it. The band's own attribute is the state.
   */
  private applyCollapse(collapsed: boolean): void {
    if (this.bar) this.bar.collapsed = collapsed;
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.observer?.disconnect();
    this.bandWatch?.stop();
  }

  override firstUpdated(): void {
    if (!this.sentinel) return;
    // Default root: the viewport. Intersection accounts for clipping ancestors,
    // so the shell's scroll container is handled without naming it here. A hidden
    // workspace generates no boxes, so a backgrounded Library cannot page.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void this.loadMore();
      },
      { rootMargin: '600px' },
    );
    this.observer.observe(this.sentinel);
  }

  /**
   * A *new* query resets the surface. The initial one is not a new query.
   *
   * Lit seeds the first update's `changedProperties` with every initialised
   * property, so `query` always arrives here as a change from `undefined` to
   * `''` — on every mount, before the user has done anything. Read as a change
   * it means "the user just cleared the search", and `restartSearch` answers
   * that by refetching page 0, which is the second of the three concurrent cold
   * loads ADR 0004 §6b unpicks.
   *
   * The seed is identified by both halves: no previous value AND nothing in the
   * query. Testing only for the missing previous value would swallow a genuine
   * query that arrived before the first render — nothing sets one today (the
   * shell seeds from `queries`, which only `onQuery` fills, and the router has
   * no `q` param), but a deep link into a search is an obvious thing to add and
   * it should not land as silence.
   *
   * §6b's guards would each catch the duplicate anyway. This is where it stops
   * being a duplicate that needs catching.
   */
  override willUpdate(changed: Map<string, unknown>): void {
    if (!changed.has('query')) return;
    const seeding = changed.get('query') === undefined && !this.query;
    if (seeding) return;
    this.toTop();
    void this.restartSearch();
  }

  /**
   * Back to the top, because the content set has been replaced.
   *
   * §11 preserves state across *navigation*; a new query or category is a new set,
   * and keeping the offset drops you into the middle of a list you have not seen
   * the start of.
   *
   * A direct write, not `scrollIntoView`: the host IS the scrollport since ADR 0010
   * gave every workspace its own, and asking an ancestor to scroll this into view
   * does nothing when the ancestor is `overflow: hidden`.
   */
  private toTop(): void {
    this.scrollTop = 0;
  }

  /** Whichever mode is showing gets the next page. */
  private loadMore(): Promise<void> {
    return this.searching ? this.loadSearchPage() : this.loadGridPage();
  }

  /**
   * Reload whichever mode is showing, from the top. The shell's pull-to-refresh
   * calls this — see `WorkspaceDef.refreshable` and ADR 0010.
   *
   * Refetching the counts alongside the rows is the point rather than a bonus:
   * the queue drains in the background, so "there are new reels" shows up in the
   * chip totals as much as in the grid, and refreshing one without the other
   * leaves the two disagreeing.
   *
   * Bumping `pageToken` first means any page still in flight discards its own
   * result, so a refresh during paging cannot append a stale page on top of the
   * fresh one. It does not cancel that request, but the bump is also what gets
   * the refetch below past the mutex: a load carrying a token newer than the one
   * in flight is a supersession, and those are never refused — so an overlap
   * costs one wasted response, not a refresh that does nothing.
   */
  async refresh(): Promise<void> {
    this.error = '';
    if (this.searching) {
      await Promise.all([this.loadLibraryCounts(), this.restartSearch()]);
      return;
    }
    this.pageToken++;
    this.gridOffset = 0;
    this.gridExhausted = false;
    await Promise.all([this.loadLibraryCounts(), this.loadGridPage()]);
  }

  private async loadLibraryCounts(): Promise<void> {
    try {
      const meta = await getMeta();
      if (!isSignedIn(meta)) return;
      // Server orders by count already; All carries the library total so the
      // chip row reads as a breakdown of one number.
      const total = meta.total ?? meta.categories.reduce((n, c) => n + c.n, 0);
      this.libraryCats = [
        { value: '', label: 'All', n: total },
        ...meta.categories.map((c) => ({ value: c.name, label: clean(c.name) || c.name, n: c.n })),
      ];
    } catch {
      /* The chips are a filter, not the content. Losing them must not blank the
         grid, and the error the grid's own fetch raises would say the same thing
         twice. */
    }
  }

  private async loadGridPage(): Promise<void> {
    if (this.gridExhausted || this.searching) return;
    // Same token as the request already running: a duplicate, not a supersession.
    if (this.gridPaging && this.gridInFlight === this.pageToken) return;
    this.gridPaging = true;
    const mine = this.pageToken;
    this.gridInFlight = mine;
    // The offset this request ASKED for, not the live cursor. Read at settle
    // time it says whatever an earlier response already moved it to, so a second
    // page-0 load would append its own rows on top of the identical set the
    // first one just installed.
    const at = this.gridOffset;
    try {
      const rows = await getItems(this.category, at);
      if (mine !== this.pageToken) return; // category changed mid-flight
      if (isSignedIn(rows)) {
        this.items = at === 0 ? rows : [...this.items, ...rows];
        this.gridOffset = at + rows.length;
        if (rows.length < PAGE_SIZE) this.gridExhausted = true;
      }
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      // Only the newest request settles either flag: a stale one landing late
      // would otherwise open the gate on the append that superseded it. It can
      // never wedge the mutex, because whatever bumped the token past `mine`
      // took the mutex itself and will release it in turn.
      if (mine === this.pageToken) {
        this.gridLoading = false;
        this.gridPaging = false;
      }
    }
  }

  private async loadSearchPage(): Promise<void> {
    const q = this.query.trim();
    if (this.searchExhausted || !q) return;
    // Token-keyed, and the offset bound below — see the note on `searchPaging`.
    // A cold search is exposed exactly as the grid was: the sentinel can fire
    // behind the skeletons while restartSearch's own first page is still out.
    if (this.searchPaging && this.searchInFlight === this.searchToken) return;
    this.searchPaging = true;
    const mine = this.searchToken;
    this.searchInFlight = mine;
    const at = this.searchOffset;
    try {
      const rows = await searchItems(q, this.category, at);
      if (mine !== this.searchToken) return; // query or category moved on
      if (isSignedIn(rows)) {
        this.results = at === 0 ? rows : [...this.results, ...rows];
        this.searchOffset = at + rows.length;
        if (rows.length < SEARCH_PAGE_SIZE) this.searchExhausted = true;
      }
    } catch (e) {
      if (mine === this.searchToken) this.error = e instanceof Error ? e.message : String(e);
    } finally {
      // Newest-only, as in loadGridPage — see the note on `searchPaging`.
      if (mine === this.searchToken) {
        this.searchLoading = false;
        this.searchPaging = false;
      }
    }
  }

  /** New query: throw away the old result set and its facets, then refetch both. */
  private async restartSearch(): Promise<void> {
    const q = this.query.trim();
    this.searchToken++;
    this.results = [];
    this.searchOffset = 0;
    this.searchExhausted = false;
    this.error = '';
    if (!q) {
      this.searchLoading = false;
      // Usually free: the grid is still in `items` and the sentinel resumes
      // paging where it left off. Not free after a category change made *while*
      // searching, which resets both cursors but only refetches the mode on
      // screen — the grid comes back empty, and the sentinel cannot rescue it
      // because it has been continuously intersecting and so never fires again.
      //
      // `gridPaging` belongs in the test because an empty `items` also describes
      // a cold load still in flight, which is going to fill it. Without that this
      // fires on every single mount — Lit puts `query` in the first update's
      // changedProperties, so `willUpdate` calls us before connectedCallback's
      // own fetch has landed — and asks for page 0 a second time. Skipping here
      // rather than bumping `pageToken` and superseding: there is nothing wrong
      // with the request already out, and discarding it only pays for it twice.
      // Nothing owns `gridLoading` that we are leaving set, either — the request
      // we defer to owns it, and it is already true from its initial value.
      if (!this.items.length && !this.gridExhausted && !this.gridPaging) {
        this.gridLoading = true;
        void this.loadGridPage();
      }
      return;
    }
    this.searchLoading = true;
    // Cleared rather than left in place: stale counts describing the previous
    // query would be a confident wrong answer, and the bar's min-height keeps the
    // layout still while the new ones land.
    this.facets = null;
    this.facetsFor = q;
    const mine = this.searchToken;
    void getSearchFacets(q)
      .then((f) => {
        if (mine === this.searchToken && isSignedIn(f)) this.facets = f;
      })
      .catch(() => {
        /* The chips are a filter, not the content — the result list's own error
           reporting covers a genuine outage. */
      });
    await this.loadSearchPage();
  }

  /**
   * Category applies to both modes, so both cursors reset.
   *
   * Facets are deliberately NOT refetched: they count how the current query's
   * matches distribute across *every* category, which does not change when the
   * user picks one of them. Refetching would make the chips flicker to identical
   * numbers.
   */
  private pickCategory(value: string): void {
    if (value === this.category) return;
    this.category = value;
    this.pageToken++;
    this.searchToken++;
    this.items = [];
    this.gridOffset = 0;
    this.gridExhausted = false;
    this.results = [];
    this.searchOffset = 0;
    this.searchExhausted = false;
    this.toTop();
    if (this.searching) {
      this.searchLoading = true;
      void this.loadSearchPage();
    } else {
      this.gridLoading = true;
      void this.loadGridPage();
    }
  }

  /**
   * The chip row for the active mode.
   *
   * Searching, the counts come from the facets, and only categories with matches
   * get a chip — with one exception: a selected category that has none still
   * renders, at zero. Dropping it would remove the only control that shows the
   * filter is on, leaving an empty list with no visible cause.
   */
  private get chips(): ChipOption[] {
    if (!this.searching) return this.libraryCats;
    const f = this.facets;
    if (!f || this.facetsFor !== this.query.trim()) return [];
    const rows = f.categories.map((c) => ({
      value: c.name,
      label: clean(c.name) || c.name,
      n: c.n,
    }));
    if (this.category && !rows.some((r) => r.value === this.category)) {
      rows.push({ value: this.category, label: clean(this.category) || this.category, n: 0 });
    }
    return [{ value: '', label: 'All', n: f.total }, ...rows];
  }

  /**
   * Open Detail on `item`, with everything currently on screen as the playlist.
   *
   * The whole list, not just the one item: the arrows in Detail then walk the
   * same set the user was looking at, in the same order — including the search
   * ranking, when they came from results.
   */
  private openItem(list: Item[], item: Item): void {
    // Library is the one origin whose thread can be two things at once. The
    // query wins because it is the narrower of the two — a category chip
    // applied on top of a search describes the list far less than the search
    // does. With neither set the list is simply "everything", which is not a
    // thread, so nothing is sent and Detail falls back to the workspace name.
    const q = this.query.trim();
    const label = q ? `“${q}”` : this.category ? clean(this.category) || this.category : '';
    this.dispatchEvent(
      new CustomEvent('open-detail', {
        detail: {
          ids: list.map((i) => i.id),
          at: Math.max(0, list.indexOf(item)),
          source: label ? { workspace: 'library', label } : undefined,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  applyIntent(intent: Intent): void {
    // The tab bar's search entry (ADR 0019). Nothing to prepare here — the band is
    // always rendered, and it reveals itself if the scroll had collapsed it.
    if (intent.kind === 'open-search') return void this.bar?.openSearch();
    // Detail removed an item; drop it from both modes rather than refetching.
    if (intent.kind !== 'item-deleted') return;
    this.items = this.items.filter((i) => i.id !== intent.id);
    this.results = this.results.filter((i) => i.id !== intent.id);
  }

  private renderSkeletons() {
    return html`<div class="grid">
      ${Array.from({ length: 18 }, () => html`<div class="skel"></div>`)}
    </div>`;
  }

  private renderResults() {
    if (this.searchLoading && !this.results.length) return this.renderSkeletons();
    if (!this.results.length) {
      return html`<g-notice class="page">
        ${this.category
          ? html`Nothing in ${this.category} matches “${this.query.trim()}” — the chips
              above show which categories do.`
          : html`Nothing matches “${this.query.trim()}”. Search covers what was said,
              written on screen, and captioned.`}
      </g-notice>`;
    }
    return html`
      <div class="results">
        <p class="rank">ranked by match</p>
        ${this.results.map(
          (it) => html`
            <item-row .item=${it} @open=${() => this.openItem(this.results, it)}></item-row>
          `,
        )}
      </div>
    `;
  }

  private renderGrid() {
    if (this.gridLoading) return this.renderSkeletons();
    if (!this.items.length) {
      return html`<g-notice class="page">
        ${this.category
          ? html`Nothing in ${this.category} yet.`
          : 'Nothing here yet — share a reel to your webhook and it lands in this grid once analysed.'}
      </g-notice>`;
    }
    return html`
      <div class="grid">
        ${this.items.map(
          (it) => html`
            <item-tile .item=${it} @open=${() => this.openItem(this.items, it)}></item-tile>
          `,
        )}
      </div>
    `;
  }

  override render() {
    return html`
      <!-- No .collapsed binding: applyCollapse owns that property, so a render
           triggered by anything else cannot fight it. -->
      <g-filter-bar
        .options=${this.chips}
        .selected=${this.category}
        .searchable=${true}
        .placeholder=${libraryDef.searchPlaceholder ?? ''}
        .query=${this.query}
        @select=${(e: CustomEvent<string>) => this.pickCategory(e.detail)}
        @search-close=${() => this.bandWatch?.reset()}
      ></g-filter-bar>

      ${this.error ? html`<g-notice class="page" tone="danger">${this.error}</g-notice>` : ''}
      ${this.searching ? this.renderResults() : this.renderGrid()}
      <div class="sentinel"></div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-library': GLibrary;
  }
}
